import { useEffect, useRef } from "react";

/**
 * Fires onLoadMore when the sentinel enters view.
 * Uses a short cooldown after each trigger so a still-visible sentinel cannot
 * cascade page fetches while images from the previous page are still starting.
 */
export function useInfiniteScroll({ hasNext, loading, loadingMore, onLoadMore, cooldownMs = 500 }) {
    const sentinelRef = useRef(null);
    const lastTriggerAtRef = useRef(0);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting || !hasNext || loading || loadingMore) {
                    return;
                }
                const now = Date.now();
                if (now - lastTriggerAtRef.current < cooldownMs) {
                    return;
                }
                lastTriggerAtRef.current = now;
                onLoadMore();
            },
            { rootMargin: "120px" }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasNext, loading, loadingMore, onLoadMore, cooldownMs]);

    return sentinelRef;
}
